import { useState } from 'react'
import { Eye, Search, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

interface Mention {
  id: number
  title: string
  summary: string
  source: string
  url: string
  category: string
  published_at: string | null
  cves: string[]
  actors: string[]
}

export default function BrandMentionsPage() {
  const [brand, setBrand] = useState('')
  const [days, setDays] = useState(14)
  const [data, setData] = useState<{ mentions: Mention[]; by_source: Record<string, number>; total: number } | null>(null)
  const [loading, setLoading] = useState(false)

  const search = async () => {
    if (brand.trim().length < 2) return
    setLoading(true)
    try {
      const r = await api.get('/api/cyber/brand-mentions', { params: { brand: brand.trim(), days, limit: 100 } })
      setData(r.data)
    } catch { setData(null) } finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Eye size={24} className="text-cyan" /> Brand Mention Monitor
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Find mentions of any company / product / brand across the cyber + tech news corpus, enriched with linked CVEs + actor mentions.
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input value={brand} onChange={e => setBrand(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && search()}
              placeholder="e.g. Cloudflare, Snowflake, Capita…"
              className="input pl-9 py-2 text-sm w-full" />
          </div>
          <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))} className="input py-2 text-xs">
            <option value={3}>last 3d</option>
            <option value={7}>last 7d</option>
            <option value={14}>last 14d</option>
            <option value={30}>last 30d</option>
            <option value={90}>last 90d</option>
          </select>
          <button type="button" onClick={search} className="btn-primary text-sm" disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </div>

      {data && data.total === 0 && (
        <div className="card p-6 text-center text-text-muted text-sm">
          No mentions of "{brand}" in the last {days} days. The article corpus enriches over time — check back tomorrow.
        </div>
      )}

      {data && data.total > 0 && (
        <>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">By source</div>
            <div className="flex flex-wrap gap-2 text-[11px]">
              {Object.entries(data.by_source).map(([s, n]) => (
                <span key={s} className="px-2 py-0.5 rounded bg-surface-raised border border-border">
                  {s} <span className="font-mono text-blue-light">{n}</span>
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.mentions.map(m => (
              <a key={m.id} href={m.url} target="_blank" rel="noreferrer noopener"
                className="card p-3 hover:bg-surface-hover transition-colors flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono text-blue-light truncate">{m.source}</span>
                  {m.published_at && (
                    <span className="text-[10px] text-text-muted whitespace-nowrap">
                      {formatDistanceToNow(new Date(m.published_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-medium text-text-primary leading-snug line-clamp-2">{m.title}</h3>
                {m.summary && <p className="text-[11px] text-text-secondary line-clamp-2">{m.summary}</p>}
                {(m.cves.length > 0 || m.actors.length > 0) && (
                  <div className="flex flex-wrap gap-1 text-[9px] mt-auto">
                    {m.cves.map(c => (
                      <Link key={c} to={`/app/cve/${encodeURIComponent(c)}`}
                        onClick={e => e.stopPropagation()}
                        className="font-mono px-1.5 py-0.5 rounded bg-rose/10 text-rose-light border border-rose/30">
                        {c}
                      </Link>
                    ))}
                    {m.actors.map(a => (
                      <span key={a} className="font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30">{a}</span>
                    ))}
                  </div>
                )}
                <ExternalLink size={9} className="text-text-muted ml-auto" />
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
